/**
 * Flow Optimizer — legacy group-weight migration to per-layer shape
 * For Media Devoted | Created By Ciro Autiero
 *
 * Configs saved before per-layer weights stored ONE flat weight per group
 * (`Record<string, number>`). On read we upgrade them: each group's old weight
 * is copied to every layer the group `appliesTo` (undefined => all layers).
 * Already-nested per-layer entries pass through (trimmed to the applicable
 * layers), so the migration is idempotent and safe to run on every read.
 */

import {
  ALL_LAYERS,
  type FlowLayer,
  type FormulaGroup,
  type GroupWeights,
  type PerLayerGroupWeight,
} from "../voluum-flow-optimizer.types";

type GroupScope = Pick<FormulaGroup, "id" | "appliesTo">;

export function migrateGroupWeightsToPerLayer(
  gw: GroupWeights,
  groups: ReadonlyArray<GroupScope>,
): Record<string, PerLayerGroupWeight> {
  const out: Record<string, PerLayerGroupWeight> = {};
  for (const group of groups) {
    out[group.id] = migrateOne(gw[group.id], layersFor(group));
  }
  return out;
}

function layersFor(group: GroupScope): ReadonlyArray<FlowLayer> {
  const scope = group.appliesTo;
  return Array.isArray(scope) && scope.length > 0 ? scope : ALL_LAYERS;
}

function migrateOne(
  entry: PerLayerGroupWeight | number | undefined,
  layers: ReadonlyArray<FlowLayer>,
): PerLayerGroupWeight {
  const flat = typeof entry === "number" ? entry : 0;
  const nested = entry != null && typeof entry === "object" ? entry : null;
  const out: PerLayerGroupWeight = {};
  for (const layer of layers) {
    const value = nested ? nested[layer] : flat;
    out[layer] = typeof value === "number" ? value : flat;
  }
  return out;
}
